import { useCallback, useEffect, useMemo, useState } from 'react';
import { Menu, Plus, Search } from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { Sidebar } from '@/components/layout/Sidebar';
import { QuickAdd } from '@/components/tasks/QuickAdd';
import { TimerBar } from '@/components/tasks/TimerBar';
import { TaskDetailPanel } from '@/components/tasks/TaskDetail';
import { useLanguageSync, useSettings } from '@/hooks/useAuth';
import { useHotkeys, type Hotkey } from '@/hooks/useHotkeys';
import type { UserMe } from '@/lib/api/types';
import { CommandPalette } from '@/components/layout/CommandPalette';
import { Logo } from '@/components/ui/logo';
import { useIsCompact } from '@/hooks/useMediaQuery';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * The application frame: sidebar, the routed screen, and the two overlays that
 * must be reachable from anywhere — Quick Add and the task detail.
 *
 * The open task lives here rather than in the URL for now: a task can be opened
 * from any screen, and returning to that screen with the dialog closed is the
 * behaviour people expect from a peek.
 */
export interface ShellContext {
  currentUser: UserMe;
  openTask: (taskId: string) => void;
  openQuickAdd: () => void;
}

export function AppShell({ user }: { user: UserMe }) {
  // The account's language wins over whatever the browser advertises, so the
  // same person gets the same interface on a borrowed machine.
  useLanguageSync();
  const navigate = useNavigate();
  const { data: settings } = useSettings();

  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const isCompact = useIsCompact();
  const location = useLocation();
  const t = useT();

  // Choosing a destination is the end of the drawer's job. Keyed on the path
  // rather than on each link's onClick, so nothing added later can forget to
  // do it. Adjusted during render with a change guard — the same pattern the
  // KB column uses, and one render rather than the extra pass an effect costs.
  const [lastPath, setLastPath] = useState(location.pathname);
  if (lastPath !== location.pathname) {
    setLastPath(location.pathname);
    setNavOpen(false);
  }

  // A drawer over a scrollable page scrolls the page behind it under a finger.
  useEffect(() => {
    if (!navOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [navOpen]);

  const openTask = useCallback((taskId: string) => setOpenTaskId(taskId), []);
  const openQuickAdd = useCallback(() => setQuickAddOpen(true), []);

  /**
   * Global shortcuts (spec §45–46).
   *
   * `⌘K` opens the command palette, so plain `n` and `q` open Quick Add — the
   * spec asks for an alternative precisely because ⌘K is taken.
   */
  const hotkeys = useMemo<Hotkey[]>(
    () => [
      { key: 'k', meta: true, allowInInput: true, handler: () => setPaletteOpen(true) },
      { key: 'n', handler: () => setQuickAddOpen(true) },
      { key: 'q', handler: () => setQuickAddOpen(true) },
      { key: 'g', handler: () => navigate('/today') },
    ],
    [navigate],
  );
  useHotkeys(hotkeys);

  const context: ShellContext = { currentUser: user, openTask, openQuickAdd };

  return (
    <div className="flex h-full">
      <Sidebar user={user} onOpenSearch={() => setPaletteOpen(true)} open={navOpen} />

      {/* The drawer's backdrop. Rendered only while open so it cannot swallow
          clicks on desktop, where the sidebar is a column and not a layer. */}
      {isCompact && navOpen ? (
        <button
          type="button"
          aria-label={t('common.close')}
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
        />
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* The phone's only chrome: what the sidebar was holding — the way into
            navigation, search and capture — in a 52px bar. Above `lg` the
            sidebar is back and this disappears. */}
        <header
          className={cn(
            'flex shrink-0 items-center gap-1 border-b border-border bg-sidebar',
            'px-2 py-2 text-sidebar-content lg:hidden',
            // Below the notch on a phone, not under it.
            'pt-[max(0.5rem,env(safe-area-inset-top))]',
          )}
        >
          <button
            type="button"
            onClick={() => setNavOpen(true)}
            aria-label={t('nav.label')}
            className="rounded-lg p-2.5 hover:bg-sidebar-hover"
          >
            <Menu className="h-5 w-5" aria-hidden />
          </button>

          <Logo
            markLight
            markClassName="h-[17px]"
            nameClassName="text-sidebar-content"
            accentClassName="text-sidebar-accent"
          />

          <div className="flex-1" />

          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-label={t('common.search')}
            className="rounded-lg p-2.5 hover:bg-sidebar-hover"
          >
            <Search className="h-5 w-5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={openQuickAdd}
            aria-label={t('quickAdd.title')}
            className="rounded-lg bg-accent p-2.5 text-white hover:bg-accent-hover"
          >
            <Plus className="h-5 w-5" aria-hidden />
          </button>
        </header>

        {/* A running clock, directly under the phone's bar. The sidebar is a
            drawer here, so a clock living only in it was invisible exactly
            where it is most often forgotten. Renders nothing while idle. */}
        <TimerBar variant="header" />

        {/* Three panes, as in the macOS app: sidebar, the day, the detail.
            The old top bar is gone — search lives as a floating control so the
            headline of every screen is the screen itself. */}
        <main className="relative flex min-h-0 min-w-0 flex-1">
          <div className="scroll-area min-w-0 flex-1 bg-surface-sunken">
            <Outlet context={context} />
          </div>

          <TaskDetailPanel
            onOpenTask={openTask}
            taskId={openTaskId}
            currentUserId={user.id}
            onClose={() => setOpenTaskId(null)}
          />
        </main>
      </div>

      <QuickAdd
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        defaultDestination={settings?.default_task_destination ?? 'today'}
      />

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        onOpenTask={openTask}
        onCreateTask={openQuickAdd}
      />
    </div>
  );
}
